import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Rectangle, Polygon, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { renderToStaticMarkup } from 'react-dom/server';
import axios from 'axios';
import { Car, Coffee, Info, TreePine, Zap, ShowerHead, Toilet, Waves } from 'lucide-react';
import MapLibreCampMap from './MapLibreCampMap';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PARCEL_W = 0.00008;
const DEFAULT_PARCEL_H = 0.0001;

const AMENITY_TYPES = {
  toilet: { Icon: Toilet, color: '#3B82F6', label: 'WC' },
  shower: { Icon: ShowerHead, color: '#06B6D4', label: 'Tuš' },
  parking: { Icon: Car, color: '#6366F1', label: 'Parking' },
  beach: { Icon: Waves, color: '#F59E0B', label: 'Plaža' },
  restaurant: { Icon: Coffee, color: '#EF4444', label: 'Restoran' },
  reception: { Icon: Info, color: '#10B981', label: 'Recepcija' },
  playground: { Icon: TreePine, color: '#84CC16', label: 'Igralište' },
  electricity: { Icon: Zap, color: '#F97316', label: 'Struja' },
};

// Read the active brand color from the document so the selected-parcel highlight
// matches whatever the admin has configured (set by ThemeContext at runtime).
const readBrandColor = () => {
  if (typeof window === 'undefined') return '#1D4ED8';
  const v = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
  return v || '#1D4ED8';
};

const lucideToHtml = (IconComp, size = 16) =>
  renderToStaticMarkup(<IconComp color="#fff" size={size} strokeWidth={2.25} />);

const createAmenityIcon = (type) => {
  const info = AMENITY_TYPES[type] || { Icon: Info, color: '#999', label: type };
  const inner = lucideToHtml(info.Icon, 16);
  return L.divIcon({
    className: 'camp-amenity-icon',
    html: `<div style="background:${info.color};color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.35);border:2px solid #fff;">${inner}</div>`,
    iconSize: [32, 32], iconAnchor: [16, 16],
  });
};

const createLabel = (number, color, selectedColor, selected) => L.divIcon({
  className: 'camp-parcel-num',
  html: `<div style="background:${selected ? selectedColor : color};color:#fff;padding:1px 5px;border-radius:3px;font-weight:700;font-size:11px;box-shadow:0 1px 3px rgba(0,0,0,0.4);min-width:20px;text-align:center;${selected ? 'border:2px solid #fff;' : ''}">${number}</div>`,
  iconSize: [28, 18], iconAnchor: [14, 9],
});

function getRectangleBounds(p) {
  const w = p.width || DEFAULT_PARCEL_W;
  const h = p.height || DEFAULT_PARCEL_H;
  return [[p.lat - h / 2, p.lng - w / 2], [p.lat + h / 2, p.lng + w / 2]];
}

function getPolygonCentroid(coords) {
  let lat = 0, lng = 0;
  coords.forEach(([la, ln]) => { lat += la; lng += ln; });
  return [lat / coords.length, lng / coords.length];
}

function FitBounds({ parcels }) {
  const map = useMap();
  useEffect(() => {
    if (parcels.length === 0) return;
    const points = [];
    parcels.forEach(p => {
      if (p.coords && p.coords.length >= 3) p.coords.forEach(c => points.push(c));
      else points.push([p.lat, p.lng]);
    });
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 19 });
  }, [parcels, map]);
  return null;
}

// Lightweight in-memory cache for room media images so the mobile popup
// doesn't refetch each time the user opens it.
const mediaCache = new Map();
async function fetchRoomImage(roomId) {
  if (mediaCache.has(roomId)) return mediaCache.get(roomId);
  try {
    const res = await axios.get(`${API}/api/rooms/${roomId}/media`);
    const img = res.data?.images?.[0] || null;
    mediaCache.set(roomId, img);
    return img;
  } catch {
    mediaCache.set(roomId, null);
    return null;
  }
}

export default function CampMapView({ properties = [], onSelectRoom, onParcelClick, onDatesChange, onOutOfFilterClick, allowedRoomIds = null, mobileMode = false, checkIn, checkOut }) {
  // Check if the admin has uploaded & activated a custom MapLibre map package.
  // If yes, delegate rendering to MapLibreCampMap which loads the user's
  // style.json (vector tiles + custom sprites) instead of OSM via Leaflet.
  const [customMap, setCustomMap] = useState(null); // { active, style_url, center, zoom, min_zoom, max_zoom }
  useEffect(() => {
    axios.get(`${API}/api/camp-map/custom-status`).then(res => {
      if (res.data?.active) setCustomMap(res.data);
      else setCustomMap({ active: false });
    }).catch(() => setCustomMap({ active: false }));
  }, []);

  const [allParcels, setAllParcels] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [mapSettings, setMapSettings] = useState({ lat: 45.327, lng: 14.442, zoom: 18 });
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popupImage, setPopupImage] = useState(null);
  const [brandColor, setBrandColor] = useState(readBrandColor);

  // Keep the selected-state color in sync with theme changes.
  useEffect(() => {
    setBrandColor(readBrandColor());
    const obs = new MutationObserver(() => setBrandColor(readBrandColor()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/camp-map`).then(res => {
      setAllParcels(res.data.parcels || []);
      setMarkers(res.data.markers || []);
      if (res.data.settings?.lat) setMapSettings(res.data.settings);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // Map of available room_id -> {property, room}
  const roomMap = useMemo(() => {
    const map = new Map();
    properties.forEach(prop => {
      (prop.available_rooms || []).forEach(room => {
        const rid = room.id || room.roomId || room.room_id;
        if (rid != null) map.set(String(rid), { property: prop, room });
      });
    });
    return map;
  }, [properties]);

  const parcels = useMemo(() => {
    return allParcels.filter(p => p.room_id && roomMap.has(String(p.room_id)));
  }, [allParcels, roomMap]);

  const zoneLegend = useMemo(() => {
    const m = new Map();
    parcels.forEach(p => {
      const color = p.color || '#22C55E';
      const name = (p.zone_name || '').trim() || 'Slobodno';
      const key = `${color}|${name}`;
      if (!m.has(key)) m.set(key, { color, name, count: 0 });
      m.get(key).count += 1;
    });
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [parcels]);

  const getPropertyRoom = useCallback(
    (parcel) => roomMap.get(String(parcel.room_id)) || null,
    [roomMap]
  );

  const handleParcelClick = (parcel) => {
    setSelectedParcel(parcel);
    setPopupImage(null);
    const pr = getPropertyRoom(parcel);
    if (!pr) return;
    // Desktop: tell parent to scroll & highlight the matching room card.
    // Mobile: stay in the map, just open a popup with details + "Vidi više" CTA.
    if (!mobileMode && onParcelClick) {
      onParcelClick(pr.property, pr.room);
    }
    if (mobileMode && parcel.room_id) {
      fetchRoomImage(parcel.room_id).then(setPopupImage);
    }
  };

  const handleViewMore = (parcel) => {
    const pr = getPropertyRoom(parcel);
    if (pr && onParcelClick) onParcelClick(pr.property, pr.room);
  };

  const formatPrice = (room) => {
    const base = parseFloat(room?.base_price ?? room?.price);
    if (!base || isNaN(base)) return null;

    // Compute number of nights from checkIn/checkOut (YYYY-MM-DD strings).
    let nights = 1;
    if (checkIn && checkOut) {
      const ci = new Date(checkIn);
      const co = new Date(checkOut);
      const diff = Math.round((co - ci) / (1000 * 60 * 60 * 24));
      if (diff > 0) nights = diff;
    }

    // Pick the highest discount rate the stay qualifies for (mirrors RoomCard logic).
    let bestDiscount = 0;
    (room.rates || []).forEach(rate => {
      if (rate.active === false) return;
      const minN = rate.min_nights || 1;
      const maxN = rate.max_nights || Infinity;
      if (nights >= minN && nights <= maxN && (rate.discount_percent || 0) > bestDiscount) {
        bestDiscount = rate.discount_percent;
      }
    });
    const finalPrice = base * (1 - bestDiscount / 100);
    return `od €${Math.round(finalPrice)}`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" style={{ color: 'var(--color-text-muted)' }}>Ucitavanje kamp mape...</div>;
  }

  // When a custom MapLibre package is active, render it instead of the Leaflet map.
  // It receives the same props (properties, onParcelClick, mobileMode) so the
  // booking flow on top is identical from the parent's perspective.
  if (customMap?.active && customMap.style_url) {
    return (
      <MapLibreCampMap
        styleUrl={customMap.style_url}
        center={customMap.center}
        zoom={customMap.zoom}
        minZoom={customMap.min_zoom}
        maxZoom={customMap.max_zoom}
        properties={properties}
        onParcelClick={onParcelClick}
        onDatesChange={onDatesChange}
        onOutOfFilterClick={onOutOfFilterClick}
        allowedRoomIds={allowedRoomIds}
        mobileMode={mobileMode}
      />
    );
  }

  return (
    <div className={`${mobileMode ? 'h-full flex flex-col' : 'p-3 sm:p-5 h-full flex flex-col gap-3'}`} data-testid="camp-map-view">
      {zoneLegend.length > 0 && !mobileMode && (
        <div className="bg-white border rounded-lg p-3 shadow-sm" data-testid="zone-legend">
          <p className="text-xs font-semibold uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-muted)' }}>Legenda zona</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            {zoneLegend.map((z, i) => (
              <span key={i} className="flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <span className="inline-block w-4 h-4 rounded border border-white shadow"
                  style={{ background: z.color }} />
                <span className="font-medium">{z.name}</span>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>({z.count})</span>
              </span>
            ))}
            <span className="flex items-center gap-2 ml-auto" style={{ color: 'var(--color-text-primary)' }}>
              <span className="inline-block w-4 h-4 rounded border-2 border-white"
                style={{ background: brandColor }} />
              <span className="font-medium">Odabrano</span>
            </span>
          </div>
        </div>
      )}

      <div className={`relative overflow-hidden border ${mobileMode ? 'flex-1' : 'rounded-xl shadow-lg'}`}
        style={mobileMode
          ? { height: '100%', borderColor: 'var(--color-border)' }
          : { flex: 1, minHeight: '420px', borderColor: 'var(--color-border)' }}>
        <MapContainer center={[mapSettings.lat, mapSettings.lng]} zoom={mapSettings.zoom} maxZoom={22}
          style={{ height: '100%', width: '100%' }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap" maxZoom={22} maxNativeZoom={19} />
          <FitBounds parcels={parcels.length ? parcels : allParcels} />
          {parcels.map(parcel => {
            const isSelected = selectedParcel?.parcel_id === parcel.parcel_id;
            const fillColor = parcel.color || '#22C55E';
            const strokeColor = isSelected ? brandColor : fillColor;
            const pathOpts = {
              color: strokeColor,
              weight: isSelected ? 3 : 2,
              fillOpacity: isSelected ? 0.55 : 0.35,
              fillColor: isSelected ? brandColor : fillColor,
            };
            const labelPos = parcel.coords && parcel.coords.length >= 3
              ? getPolygonCentroid(parcel.coords)
              : [parcel.lat, parcel.lng];
            const popupNode = mobileMode
              ? renderMobilePopup(parcel, getPropertyRoom(parcel), isSelected ? popupImage : null, formatPrice, handleViewMore)
              : null;
            return (
              <React.Fragment key={parcel.parcel_id}>
                {parcel.coords && parcel.coords.length >= 3 ? (
                  <Polygon positions={parcel.coords} pathOptions={pathOpts}
                    eventHandlers={{ click: () => handleParcelClick(parcel) }}>
                    {popupNode}
                  </Polygon>
                ) : (
                  <Rectangle bounds={getRectangleBounds(parcel)} pathOptions={pathOpts}
                    eventHandlers={{ click: () => handleParcelClick(parcel) }}>
                    {popupNode}
                  </Rectangle>
                )}
                <Marker position={labelPos}
                  icon={createLabel(parcel.number, fillColor, brandColor, isSelected)}
                  eventHandlers={{ click: () => handleParcelClick(parcel) }}>
                  {popupNode}
                </Marker>
              </React.Fragment>
            );
          })}
          {markers.map(m => (
            <Marker key={m.marker_id} position={[m.lat, m.lng]} icon={createAmenityIcon(m.type)}>
              <Popup><div className="text-center text-sm font-medium">{m.label}</div></Popup>
            </Marker>
          ))}
        </MapContainer>

        {!mobileMode && (
          <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-xs font-semibold shadow z-[1000]"
            style={{ color: 'var(--color-text-primary)' }}>
            {parcels.length} dostupno
          </div>
        )}
      </div>

      {parcels.length === 0 && !mobileMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800"
          data-testid="no-available-parcels">
          Nema slobodnih parcela / smještaja za odabrane datume.
        </div>
      )}
    </div>
  );
}

// Renders a Leaflet popup body for the mobile "tap parcel" interaction.
function renderMobilePopup(parcel, propRoom, image, formatPrice, onViewMore) {
  const price = formatPrice(propRoom?.room);
  const zone = (parcel.zone_name || '').trim();
  return (
    <Popup closeButton={true} autoPan={true} className="camp-parcel-popup" minWidth={220} maxWidth={240}>
      <div className="w-[220px]" data-testid="parcel-popup">
        {image && (
          <img src={image} alt={parcel.label || `Parcela ${parcel.number}`}
            className="w-full h-24 object-cover rounded-md mb-2 -mt-1"
            onError={(e) => { e.target.style.display = 'none'; }} />
        )}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded text-white text-xs font-bold flex-shrink-0"
            style={{ background: parcel.color || '#22C55E' }}>{parcel.number}</span>
          <p className="font-semibold text-sm leading-snug m-0" style={{ color: 'var(--color-text-primary)' }}>
            {parcel.label || `Parcela ${parcel.number}`}
          </p>
        </div>
        <div className="flex items-baseline justify-between mt-1.5 mb-2">
          {zone ? (
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{zone}</span>
          ) : <span />}
          {price && (
            <span className="text-sm font-bold" style={{ color: 'var(--color-primary)' }}>{price}</span>
          )}
        </div>
        <button
          onClick={() => onViewMore(parcel)}
          className="w-full px-3 py-2 text-white rounded-md text-sm font-semibold transition-colors"
          style={{ background: 'var(--color-primary)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--color-primary-hover)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--color-primary)'}
          data-testid="parcel-popup-view-more">
          Vidi više
        </button>
      </div>
    </Popup>
  );
}
